import {
  CommonModule,
  IMAGE_LOADER,
  isPlatformBrowser,
  NgOptimizedImage,
  registerLocaleData,
} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { APP_ID, APP_INITIALIZER, LOCALE_ID, NgModule, PLATFORM_ID } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import {
  CurrentProductService,
  GlobalMessageComponentModule,
  KeyboardFocusModule,
  OutletModule,
  PageLayoutModule,
  PageSlotModule,
  SkipLinkModule,
} from 'haba-storefrontlib';
import { AppService } from 'src/app/app.service';

import { SeoService } from '@app/services';
import { GlobalMessageCustomComponentModule } from 'src/app/misc/global-message/global-message-custom.module';
import { environment } from 'src/environments/environment';

import {
  ActiveCartService,
  APP_CLIENT,
  AuthStatePersistenceService,
  BASE_SITE_CONTEXT_ID,
  BasePageMetaResolver,
  BaseSiteService,
  ContextServiceMap,
  CURRENCY_CONTEXT_ID,
  CurrencyService,
  EMPORIX_PRODUCT_NORMALIZER,
  LANGUAGE_CONTEXT_ID,
  LanguageService,
  PageMetaResolver,
  ProductPageMetaResolver,
  SiteContextConfigInitializer,
} from 'haba-core';
import { AppComponent } from 'src/app/app.component';

import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ContentfulModule } from '@app/contentful';
import { MainPipeModule } from '@app/helpers';
import {
  CloudinaryService,
  CustomSiteContext,
  HabaActiveCartService,
  HabaBaseSiteService,
  HabaCurrentProductService,
  HabaSiteContextConfigInitializer,
  httpErrorHandlerInterceptor,
  httpInterceptorsProvider,
} from '@app/services';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  BaseSiteToBrandService,
  CookieModule,
  HabaAuthStatePersistenceService,
  HabaBasePageMetaResolver,
  SVG_PATH_GETTER,
  SvgGetter,
} from 'haba-base-components';
import {
  APP_CONTENTFUL_ENVIRONMENT,
  CLOUDINARY_SERVICE,
  cloudinaryLoaderFactory,
} from 'haba-contentful-components';
import {
  API_URL,
  EmporixSdkService,
  PREFIXBOX_ENVIRONMENT_TRACKERS,
  PREFIXBOX_NEW_ARRIVALS_CATEGORY_ID,
  PrefixboxFilterCustomization,
  PrefixboxModule,
} from 'haba-emporix';
import { AgeRangeFilterCustomization } from './search/filter/age-range-filter';
import {
  RECAPTCHA_SETTINGS,
  RECAPTCHA_V3_SITE_KEY,
  RecaptchaFormsModule,
  RecaptchaModule,
  RecaptchaSettings,
  RecaptchaV3Module,
} from 'ng-recaptcha';
import { ToastrModule } from 'ngx-toastr';
import { AppRoutingModule } from 'src/app/app-routing.module';
import { CatalogModule } from 'src/app/catalog/catalog.module';
import { CustomCheckoutModule } from 'src/app/checkout/custom-checkout.module';
import { HeaderModule } from 'src/app/header/header.module';
import { NavigationModule } from 'src/app/navigation/navigation.module';
import { NewsletterModule } from 'src/app/page/newsletter/newsletter.module';
import { ProductModule } from 'src/app/product/product.module';
import { SpartacusModule } from 'src/app/spartacus/spartacus.module';
import { UserModule } from 'src/app/user/user.module';
import { AuthenticationModule } from './authentication/authentication.module';
import { DynamicGridModule } from './contentful/dynamic-grid/dynamic-grid.module';
import { HabaStorefrontComponent } from './haba-storefront/haba-storefront.component';
import { InvoiceDownloadModule } from './page/invoice-download/invoice-download.module';
import { OrderStatusModule } from './page/order-status/order-status.module';
import { CustomProductNormalizer } from './product/custom-url/custom-product.normalizer';
import { ProductAdvisorModule } from './page/product-advisor/product-advisor.module';
import { Logger } from '@opentelemetry/api-logs';
import { createOtelLogger, OTEL_LOGGER } from './utils/logging/logging';
import { patchConsoleWithOtel } from './utils/logging/console-bridge';

registerLocaleData(localeDe);

export function serviceMapFactory() {
  return {
    [LANGUAGE_CONTEXT_ID]: LanguageService,
    [CURRENCY_CONTEXT_ID]: CurrencyService,
    [BASE_SITE_CONTEXT_ID]: BaseSiteService,
    custom: CustomSiteContext,
  };
}

export function initServices(appService: AppService, logger: Logger) {
  return () => {
    patchConsoleWithOtel(logger);
    appService.initThirdPartyServices();
  };
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    SpartacusModule,
    HeaderModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    MainPipeModule,
    CommonModule,
    UserModule,
    NavigationModule,
    CatalogModule,
    CustomCheckoutModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    CookieModule.forRoot(),
    ProductModule,
    ContentfulModule,
    PageLayoutModule,
    PageSlotModule,
    GlobalMessageComponentModule,
    SkipLinkModule,
    KeyboardFocusModule,
    OutletModule,
    GlobalMessageCustomComponentModule,
    NewsletterModule,
    ServiceWorkerModule.register('haba-service-worker.js', { enabled: environment.production }),
    RecaptchaModule,
    RecaptchaFormsModule,
    RecaptchaV3Module,
    InvoiceDownloadModule,
    OrderStatusModule,
    AuthenticationModule,
    DynamicGridModule,
    StoreDevtoolsModule.instrument({ maxAge: 25, connectInZone: true }),
    PrefixboxModule,
    HabaStorefrontComponent,
    NgOptimizedImage,
    ProductAdvisorModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (emporixSdkService: EmporixSdkService, platformId: object) => () => {
        // Dynamic import: @opentelemetry/context-zone side-loads a browser zone.js,
        // which must never be evaluated on the server (it breaks the SSR process).
        if (isPlatformBrowser(platformId)) {
          return import('./utils/tracing/tracing').then(({ setupTracing }) =>
            setupTracing(emporixSdkService)
          );
        }
        return undefined;
      },
      deps: [EmporixSdkService, PLATFORM_ID],
      multi: true,
    },
    {
      provide: OTEL_LOGGER,
      useFactory: () =>
        createOtelLogger({
          serviceName: environment.opentelemetry.serviceName,
          environment: environment.opentelemetry.environment,
        }),
    },
    { provide: APP_ID, useValue: 'spartacus-app' },
    { provide: API_URL, useValue: environment.emporixsdk.apiUrl },
    { provide: APP_CLIENT, useValue: environment.client },
    { provide: PREFIXBOX_ENVIRONMENT_TRACKERS, useValue: environment.prefixbox },
    { provide: PrefixboxFilterCustomization, useClass: AgeRangeFilterCustomization },
    { provide: PREFIXBOX_NEW_ARRIVALS_CATEGORY_ID, useValue: '08000000' },
    { provide: APP_CONTENTFUL_ENVIRONMENT, useValue: environment.contentful },
    BaseSiteToBrandService,
    {
      provide: ContextServiceMap,
      useFactory: serviceMapFactory,
    },
    {
      provide: CurrentProductService,
      useClass: HabaCurrentProductService,
    },
    {
      provide: SiteContextConfigInitializer,
      useClass: HabaSiteContextConfigInitializer,
    },
    {
      provide: AuthStatePersistenceService,
      useClass: HabaAuthStatePersistenceService,
    },
    httpInterceptorsProvider,
    httpErrorHandlerInterceptor,
    { provide: CLOUDINARY_SERVICE, useExisting: CloudinaryService },
    {
      provide: APP_INITIALIZER,
      useFactory: initServices,
      deps: [AppService, OTEL_LOGGER],
      multi: true,
    },
    {
      provide: EMPORIX_PRODUCT_NORMALIZER,
      useClass: CustomProductNormalizer,
      multi: true,
    },
    {
      provide: ProductPageMetaResolver,
      useExisting: SeoService,
      multi: true,
    },
    {
      provide: PageMetaResolver,
      useExisting: SeoService,
      multi: true,
    },
    {
      provide: BasePageMetaResolver,
      useExisting: HabaBasePageMetaResolver,
    },
    {
      provide: ActiveCartService,
      useExisting: HabaActiveCartService,
    },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {
        siteKey: environment.recaptcha.v2,
        language: 'de',
      } as RecaptchaSettings,
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptcha.v3,
    },
    {
      provide: BaseSiteService,
      useExisting: HabaBaseSiteService,
    },
    provideHttpClient(withFetch()),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: SVG_PATH_GETTER,
      useFactory: () =>
        <SvgGetter>{
          get: (svgName: string, assetsPath: string) =>
            svgName ? `${assetsPath || '/assets/icons'}/${svgName}.svg#svg` : '',
        },
    },
    {
      provide: IMAGE_LOADER,
      useFactory: () => cloudinaryLoaderFactory(),
      deps: [],
    },
    // this is only used on the currencyPipe, the translations go outside of it.
    { provide: LOCALE_ID, useValue: 'de-DE' },
  ],
})
export class AppModule {}
